/**
 * Theme channel — the cross-frame `postMessage` protocol for keeping an
 * embedded portal and its host in visual sync, behind a 3-method interface.
 *
 * Split out of ThemeProvider.tsx so the risky parts (origin allow-listing,
 * echo-loop avoidance) can be tested against a fake `window`. Inbound theme
 * changes are handed to `onThemeChange` only — the caller updates state and
 * does NOT re-broadcast, which is what previously caused an echo loop.
 */

export type ThemePaletteMode = "light" | "dark";

interface ThemeChannelOptions {
  /** Origins (besides same-origin) accepted for inbound theme messages. */
  allowedOrigins: string[];
  /** Current palette mode, read when answering a child's `theme-request`. */
  getMode: () => ThemePaletteMode;
  /** Called with a mode received from a trusted frame. Must not re-broadcast. */
  onThemeChange: (mode: ThemePaletteMode) => void;
  /** Defaults to the global `window`; injectable for tests. */
  targetWindow?: Window;
}

export interface ThemeChannel {
  /** Push the current mode to every child iframe. Call on a local user toggle. */
  broadcast: (mode: ThemePaletteMode) => void;
  /** Ask the parent frame for its current mode (no-op when not embedded). */
  requestFromParent: () => void;
  /** Detach the message listener. */
  dispose: () => void;
}

const isMode = (value: unknown): value is ThemePaletteMode => value === "light" || value === "dark";

export function createThemeChannel({
  allowedOrigins,
  getMode,
  onThemeChange,
  targetWindow = window,
}: ThemeChannelOptions): ThemeChannel {
  const originAllowed = (origin: string) =>
    origin === targetWindow.location.origin || allowedOrigins.includes(origin);

  const postToChildFrames = (message: unknown) => {
    targetWindow.document.querySelectorAll("iframe").forEach((frame) => {
      try {
        frame.contentWindow?.postMessage(message, "*");
      } catch (error) {
        console.debug("themeChannel: could not post to iframe", error);
      }
    });
  };

  const handleMessage = (event: MessageEvent) => {
    const data = event.data;
    if (!data || typeof data.type !== "string") return;

    if (!originAllowed(event.origin)) {
      console.debug(`themeChannel: ignored "${data.type}" from disallowed origin ${event.origin}`);
      return;
    }

    // A trusted frame told us its mode — apply it, do not re-broadcast.
    if ((data.type === "theme-change" || data.type === "theme-response") && isMode(data.mode)) {
      onThemeChange(data.mode);
      return;
    }

    // A child frame asked for our mode — answer just that frame.
    if (data.type === "theme-request" && event.source) {
      try {
        (event.source as WindowProxy).postMessage({ type: "theme-response", mode: getMode() }, "*");
      } catch (error) {
        console.debug("themeChannel: could not answer theme-request", error);
      }
    }
  };

  targetWindow.addEventListener("message", handleMessage);

  return {
    broadcast: (mode) => postToChildFrames({ type: "theme-change", mode }),
    requestFromParent: () => {
      if (targetWindow.self === targetWindow.top) return;
      try {
        targetWindow.parent.postMessage({ type: "theme-request" }, "*");
      } catch (error) {
        console.debug("themeChannel: could not request theme from parent", error);
      }
    },
    dispose: () => targetWindow.removeEventListener("message", handleMessage),
  };
}
